import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AppShell } from '../components/AppShell'
import { apiErrorMessage, useCheckout, useTiers, type Tier } from '../lib/api'
import { useAuth } from '../lib/auth'

// Per-tier maxDays for the Phase 1 description hint — picked from the active
// user's tier so the "Up to N days" line matches their actual deadline.
function p1MaxDaysFor(tier: Tier | undefined): number | undefined {
  return tier?.p1MaxDays
}

function pct(v: number | string | undefined, fallback = '—'): string {
  if (v == null) return fallback
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return fallback
  // Backend ships fractional (0.08), but seed/log views sometimes use string %.
  // Treat anything below 1 as a fraction.
  return n < 1 ? `${(n * 100).toFixed(0)}%` : `${n.toFixed(0)}%`
}

const TIER_ORDER = ['starter', 'pro', 'elite'] as const

export function ChallengePage() {
  const { account } = useAuth()
  const checkout = useCheckout()
  const nav = useNavigate()
  const tiers = useTiers()
  const [pickingTier, setPickingTier] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const sortedTiers = useMemo<Tier[]>(() => {
    const data = tiers.data ?? []
    return [...data].sort((a, b) => TIER_ORDER.indexOf(a.code as typeof TIER_ORDER[number]) - TIER_ORDER.indexOf(b.code as typeof TIER_ORDER[number]))
  }, [tiers.data])

  const activeTier = sortedTiers.find((t) => t.code === account?.tierCode)
  const p1Days = p1MaxDaysFor(activeTier) ?? activeTier?.p1MaxDays ?? 45
  const phases = [
    { num: 1, title: 'Challenge',     desc: 'Hit your profit target while staying within drawdown rules and the minimum bet count.', meta: `Up to ${p1Days} days · main filter` },
    { num: 2, title: 'Verification',  desc: 'Lower target (+5%), tighter rules. The luck filter — proves Phase 1 wasn\'t variance.', meta: `Up to ${activeTier?.p2MaxDays ?? 30} days · 5% target` },
    { num: 3, title: 'Review & audit', desc: 'Manual quality gate. CLV, fraud, and KYC checks before the funded invitation.', meta: '5–10 business days' },
    { num: 4, title: 'Funded',         desc: 'Profit-sharing on a virtual allocation. Monthly payout cycles, scaling rewards.', meta: `Open-ended · ${activeTier ? `${((activeTier.p4ProfitSplitTrader ?? 0.7) * 100).toFixed(0)}/${(100 - (activeTier.p4ProfitSplitTrader ?? 0.7) * 100).toFixed(0)}` : '70/30'} split` },
  ]

  // Hard rules table derived from the active tier; falls back to Pro defaults
  // when the user has no active challenge yet.
  const ref = activeTier ?? sortedTiers.find((t) => t.code === 'pro') ?? sortedTiers[0]
  const rules = [
    { label: 'Max stake per bet',   value: `${pct(ref?.maxStakePct, '2%')} of starting balance`,  detail: 'Prevents single-bet luck from dominating outcomes.' },
    { label: 'Max drawdown',        value: `${pct(ref?.maxOverallDdPct, '6%')} (static floor)`,    detail: 'Largest peak-to-trough loss from starting balance. Does not move with peak.' },
    { label: 'Daily loss limit',    value: `${pct(ref?.dailyLossLimitPct, '4%')} of starting balance`, detail: 'Worst day caps you — bigger than this and the phase fails.' },
    { label: 'Odds range',          value: `${ref?.minOdds ?? '1.50'} – ${ref?.maxOdds ?? '3.00'}`,    detail: 'No long-shot prop bets, no heavy favorites.' },
    { label: 'Approved markets only', value: 'Tier-specific list',                                    detail: 'Some markets (props, futures) are excluded for the lower tiers.' },
    {
      label: 'Max bets per day',
      value: (() => {
        const starter = sortedTiers.find((t) => t.code === 'starter')?.p1MaxBetsPerDay ?? 10
        const pro     = sortedTiers.find((t) => t.code === 'pro')?.p1MaxBetsPerDay ?? 12
        const elite   = sortedTiers.find((t) => t.code === 'elite')?.p1MaxBetsPerDay ?? 15
        return `Starter: ${starter} · Pro: ${pro} · Elite: ${elite}`
      })(),
      detail: activeTier?.p1MaxBetsPerDay != null
        ? `On your tier: up to ${activeTier.p1MaxBetsPerDay} bets per calendar day.`
        : 'Daily cap enforced server-side at bet placement.',
    },
    {
      label: 'Inactivity rule',
      // The Challenge page is a reference document — show all three tier
      // windows even when the user is on one of them. Highlight the active
      // tier in the detail line below for personalised context.
      value: (() => {
        const starter = sortedTiers.find((t) => t.code === 'starter')?.inactivityDaysP1 ?? 7
        const pro     = sortedTiers.find((t) => t.code === 'pro')?.inactivityDaysP1 ?? 10
        const elite   = sortedTiers.find((t) => t.code === 'elite')?.inactivityDaysP1 ?? 14
        return `Starter: ${starter}d · Pro: ${pro}d · Elite: ${elite}d`
      })(),
      detail: activeTier?.inactivityDaysP1 != null
        ? `On your tier: 1 bet every ${activeTier.inactivityDaysP1} days minimum.`
        : 'Skipping past the window fails the phase.',
    },
  ]

  const onPick = (code: string) => {
    setPickingTier(code)
    setCheckoutError(null)
    // Hand off to the embedded Stripe Checkout page — eligibility checks run
    // server-side on /trader/checkout/embedded before the Stripe session is
    // ever created, so we just navigate and let the page surface any error.
    nav(`/checkout/embedded?tier=${encodeURIComponent(code)}`)
  }

  return (
    <AppShell page="challenge" breadcrumb={<span className="topbar__breadcrumb-current">Challenge</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Reference</span></p>
          <h1 className="page-header__title">Challenge rules &amp; tiers</h1>
          <p className="page-header__subtitle">
            The four-phase journey from challenge to funded payout. For your live progress, see the{' '}
            <Link to="/" style={{ color: 'var(--color-primary)' }}>Dashboard</Link>.
          </p>
        </div>
      </header>

      <div className="settings-content">
        <section className="settings-card">
          <header className="settings-card__header">
            <h2 className="settings-card__title">The four phases</h2>
            <p className="settings-card__description">From paid evaluation to funded profit-sharing.</p>
          </header>
          <div className="settings-card__body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--space-3)' }}>
              {phases.map((p) => (
                <article key={p.num}
                  style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-md)', padding: 'var(--space-4)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 32, height: 32, borderRadius: 999, background: 'var(--color-primary)',
                    color: 'var(--text-on-primary)', fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                    {p.num}
                  </span>
                  <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>{p.title}</h3>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 'var(--leading-snug)', marginBottom: 'var(--space-2)' }}>{p.desc}</p>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{p.meta}</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="settings-card">
          <header className="settings-card__header">
            <h2 className="settings-card__title">Tiers</h2>
            <p className="settings-card__description">Same flow, different starting balance and minimum bets.</p>
          </header>
          <div className="settings-card__body">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Tier</th><th className="is-numeric">Fee</th><th className="is-numeric">Starting balance</th>
                    <th className="is-numeric">P1 / P2 target</th><th className="is-numeric">Max DD</th>
                    <th className="is-numeric">Daily loss</th><th className="is-numeric">Min bets</th>
                    <th style={{ textAlign: 'right' }}>Action</th></tr>
                </thead>
                <tbody>
                  {sortedTiers.map((t) => {
                    const isCurrent = account?.tierCode === t.code
                    const isLower = account && TIER_ORDER.indexOf(account.tierCode as typeof TIER_ORDER[number]) > TIER_ORDER.indexOf(t.code as typeof TIER_ORDER[number])
                    const fee = Number(t.feeEur ?? t.fee ?? 0)
                    const balance = Number(t.accountSizeEur ?? t.startingBalance ?? 0)
                    return (
                      <tr key={t.code}>
                        <td style={{ fontWeight: 600 }}>
                          {t.name}
                          {t.code === 'pro' && <span className="chip chip--won" style={{ marginLeft: 'var(--space-2)' }}>Most popular</span>}
                          {isCurrent && <span className="chip" style={{ marginLeft: 'var(--space-2)' }}>Current</span>}
                        </td>
                        <td className="is-numeric numeric">€{fee}</td>
                        <td className="is-numeric numeric">€{balance.toLocaleString('en-IE')}</td>
                        <td className="is-numeric numeric">{pct(t.p1TargetPct)} / {pct(t.p2TargetPct)}</td>
                        <td className="is-numeric numeric">{pct(t.maxOverallDdPct)}</td>
                        <td className="is-numeric numeric">{pct(t.dailyLossLimitPct)}</td>
                        <td className="is-numeric numeric">{t.p1MinBetCount ?? '—'}</td>
                        <td style={{ textAlign: 'right' }}>
                          {isCurrent
                            ? <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>Active</span>
                            : isLower
                              ? <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }} title="Cannot downgrade from your current tier.">—</span>
                              : <button type="button" className="btn btn--primary btn--sm"
                                  disabled={checkout.isPending && pickingTier === t.code}
                                  onClick={() => onPick(t.code)}>
                                  {checkout.isPending && pickingTier === t.code ? 'Loading…' : (account ? 'Upgrade' : 'Choose')}
                                </button>
                          }
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {checkoutError && (
                <div className="auth-card__error" style={{ display: 'flex', marginTop: 'var(--space-3)' }}>{checkoutError}</div>
              )}
            </div>
          </div>
        </section>

        <section className="settings-card">
          <header className="settings-card__header">
            <h2 className="settings-card__title">Hard rules</h2>
            <p className="settings-card__description">Break any of these and the current phase fails. They apply to every tier.</p>
          </header>
          <div className="settings-card__body">
            <ul className="rule-list">
              {rules.map((r) => (
                <li key={r.label} className="rule-list__item rule-list__item--pass">
                  <span className="rule-list__icon" aria-hidden>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="5 13 10 18 19 7" />
                    </svg>
                  </span>
                  <div className="rule-list__body">
                    <span className="rule-list__label">{r.label}</span>
                    <span className="rule-list__detail">{r.detail}</span>
                  </div>
                  <span className="rule-list__value">{r.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </AppShell>
  )
}
